import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import { BRAND } from "@/constants/brand";
import { Target, Eye, Sparkles, Building2, ShieldCheck, MapPin, Mail, Phone } from "lucide-react";

export default function About() {
  return (
    <>
      <PageHero
        title="About HomeCure"
        subtitle={`HomeCure is the healthcare brand operated by ${BRAND.parent} (LLPIN: ${BRAND.llpin}). We are building a single, trusted platform that brings together hospitals, healthcare personnel and patients.`}
      />

      <Section eyebrow="Our story" title="Two years of pilot — ready for nationwide launch">
        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div>
            <p className="text-slate-700 leading-relaxed">
              HomeCure was born to solve a simple, urgent problem in Indian healthcare: <strong>fragmented access</strong>.
              Patients struggle to find verified personnel for home visits, families panic during medical emergencies looking for hospital beds, and good doctors and nurses lack a transparent platform to reach the people who need them.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              For the last <strong>two years</strong>, we have been operating home healthcare services on a pilot basis in <strong>Asansol, Durgapur and Bardhaman</strong> (West Bengal) — offline. The HomeCure mobile app and website now bring that experience online, with three connected sections for patients, hospitals and healthcare personnel.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              We operate strictly as an <strong>intermediary platform</strong> under Section 2(1)(w) of the IT Act, 2000. We do not provide medical services directly — we simply make trustworthy care easier to find, book and access.
            </p>
          </div>
          <div className="hc-card p-6 bg-[#E8F0FB]">
            <div className="flex items-center gap-3">
              <img src={BRAND.logoUrl} alt="HomeCure" className="w-12 h-12 object-contain" />
              <div>
                <div className="text-lg font-extrabold text-[#0F2A4A]">{BRAND.parent}</div>
                <div className="text-xs text-slate-600">LLPIN: {BRAND.llpin}</div>
              </div>
            </div>
            <ul className="mt-5 space-y-3 text-sm text-slate-700">
              <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#3780E0]" /> {BRAND.address}</li>
              <li className="flex gap-2"><Mail className="w-4 h-4 mt-0.5 text-[#3780E0]" /> {BRAND.email}</li>
              <li className="flex gap-2"><Phone className="w-4 h-4 mt-0.5 text-[#3780E0]" /> {BRAND.phone}</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section bg="light" eyebrow="Why HomeCure" title="Mission, vision &amp; values" center>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Target, title: "Our Mission", desc: "Make verified, dignified healthcare accessible at every doorstep — from emergency beds to long-term home care." },
            { icon: Eye, title: "Our Vision", desc: "Be India's most trusted intermediary connecting patients with hospitals and healthcare personnel." },
            { icon: Sparkles, title: "Our Values", desc: "Transparency in commissions, consent-first data sharing, and zero tolerance for unverified providers." },
          ].map((v, i) => (
            <div key={i} className="hc-card p-6 bg-white">
              <div className="w-12 h-12 rounded-lg bg-[#C3D9F6] flex items-center justify-center text-[#2563B3]">
                <v.icon className="w-6 h-6" />
              </div>
              <div className="mt-3 text-xl font-bold text-[#0F2A4A]">{v.title}</div>
              <div className="mt-1 text-slate-600">{v.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="How we work" title="A platform built on trust">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="hc-card p-6">
            <Building2 className="w-9 h-9 text-[#3780E0]" />
            <div className="mt-3 text-lg font-bold text-[#0F2A4A]">Intermediary, not a clinic</div>
            <p className="mt-2 text-slate-600">
              {BRAND.parent} is not a clinical establishment under the Clinical Establishments Act, 2010.
              All medical care is rendered by independently verified hospitals and personnel listed on the platform.
            </p>
          </div>
          <div className="hc-card p-6">
            <ShieldCheck className="w-9 h-9 text-[#3780E0]" />
            <div className="mt-3 text-lg font-bold text-[#0F2A4A]">Verified providers only</div>
            <p className="mt-2 text-slate-600">
              Hospitals must submit valid registration documents under the Clinical Establishments Act before going live.
              Healthcare personnel must submit valid medical/professional registration numbers, which we verify before activation.
            </p>
          </div>
        </div>
      </Section>

      <Section bg="primary" center>
        <h2 className="text-white text-3xl md:text-4xl font-extrabold">Have questions about HomeCure?</h2>
        <p className="text-white/90 mt-3">Our grievance officer {BRAND.grievanceOfficer} and team are here to help.</p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/contact" className="bg-white text-[#2563B3] hover:bg-slate-100 font-bold px-6 py-3 rounded-lg">Get in touch</Link>
        </div>
      </Section>
    </>
  );
}
